import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

// CRA injects every component's CSS into document.head, and those styles do not
// cross a shadow boundary. Copy whatever we can read into each shadow root so the
// sections render identically to their light-DOM control — otherwise you end up
// debugging missing styles instead of the thing you meant to test.
const collectDocumentCss = () =>
  Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n');
      } catch (error) {
        // Cross-origin sheet (fonts, CDN) — cssRules throws. Handled by cloneLinks.
        return '';
      }
    })
    .join('\n');

const cloneCrossOriginLinks = (root) => {
  document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    const copy = document.createElement('link');
    copy.rel = 'stylesheet';
    copy.href = link.href;
    root.appendChild(copy);
  });
};

/**
 * Renders its children inside a real shadow root.
 *
 * mode="closed" means host.shadowRoot is null for every script on the page —
 * including Kameleoon's. We keep our own reference so React can still render
 * into it, which is exactly how a real closed-shadow component behaves.
 */
const ShadowBox = ({ mode = 'open', hostTag: Host = 'div', lightChildren, children, ...hostProps }) => {
  const hostRef = useRef(null);
  // StrictMode runs effects twice, and a closed root is invisible via
  // host.shadowRoot — so we have to remember the attachment ourselves or the
  // second pass throws "already hosts a shadow tree".
  const attachedRef = useRef(null);
  const [shadowRoot, setShadowRoot] = useState(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    if (attachedRef.current) {
      setShadowRoot(attachedRef.current);
      return;
    }

    const root = host.shadowRoot || host.attachShadow({ mode });
    attachedRef.current = root;

    const style = document.createElement('style');
    style.textContent = collectDocumentCss();
    root.appendChild(style);
    cloneCrossOriginLinks(root);

    setShadowRoot(root);
  }, [mode]);

  return (
    <Host ref={hostRef} {...hostProps}>
      {lightChildren}
      {shadowRoot && createPortal(children, shadowRoot)}
    </Host>
  );
};

export default ShadowBox;
